import { lazy, Suspense, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseNumber, fmt } from "./numberInput";
import { pctOf, isWhatPct, pctChange, changeBy, reversePct, pctDifference, pctPointChange, solvePercentOf, solveChange, UNIT_SYMBOL, UNIT_NAME, type Calc, type PctUnit } from "./percentage";
import type { CalcStep } from "@/lib/calc/steps";

// Worked steps, history and the export bar load on demand.
const PercentageExtras = lazy(() => import("./PercentageExtras"));

const TOOL_INFO_CONTENT = {
  what: `Six percentage calculations in one place: finding a percentage of a number, figuring out what percentage one number is of another, and calculating percentage change between two values, plus raising or lowering a number by a percentage, working backwards to an original price, and the percentage difference between two numbers.`,
  how: `Pick the calculation you need from the tabs, enter your numbers, and the result appears instantly as you type.`,
  tip: `Percentage change and percentage difference are often confused — this tool's "% change" tab specifically measures how much a value increased or decreased relative to its starting point, which is usually what people actually mean by "percent change."`,
};

function Field({ value, onChange, label, width = "w-32" }: { value: string; onChange: (v: string) => void; label: string; width?: string }) {
  return <Input className={width} inputMode="decimal" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} />;
}

function Result({ calc, blocked, colour }: { calc: Calc | null; blocked: boolean; colour?: boolean }) {
  if (blocked) return <Card className="px-4 py-2 font-semibold text-primary">—</Card>;
  if (!calc) return <Card className="px-4 py-2 text-sm text-muted-foreground" data-testid="pc-result">Enter numbers (e.g. 1,250.50)</Card>;
  if ("error" in calc) return <Card className="px-4 py-2 text-sm text-destructive" data-testid="pc-result">{calc.error}</Card>;
  const cls = colour ? (calc.value >= 0 ? "text-green-600" : "text-red-600") : "text-primary";
  return (
    <div>
      <Card className={`px-4 py-2 font-semibold ${cls}`} data-testid="pc-result">{calc.text}</Card>
      <p className="text-xs text-muted-foreground mt-1" data-testid="pc-working">{calc.working}</p>
    </div>
  );
}

const two = (a: string, b: string, f: (x: number, y: number) => Calc): Calc | null => {
  const x = parseNumber(a), y = parseNumber(b);
  return x === null || y === null ? null : f(x, y);
};

export default function PercentageCalculator() {
  const [v, setV] = useState({ a1: "20", b1: "150", r1: "", a2: "30", b2: "120", a3: "50", b3: "75", c3: "", a4: "100", b4: "20", a5: "80", b5: "20", a6: "50", b6: "100" });
  const [tab, setTab] = useState("pctof");
  const [unit, setUnit] = useState<PctUnit>("pct");
  const [asPoints, setAsPoints] = useState(false);
  const set = (k: keyof typeof v) => (val: string) => setV((p) => ({ ...p, [k]: val }));
  const [dir, setDir] = useState<"up" | "down">("up");
  const [rdir, setRdir] = useState<"up" | "down">("down");

  // Confirmed as a real, reported gap: a QA retest found this tool
  // still produced live results with no block at the daily limit.
  const { blocked, gate } = useLogToolUsage(true, 3000);

  // Empty box = the one to solve. A box with unreadable text counts as "not a number yet".
  const opt = (s: string): number | null => (s.trim() === "" ? null : parseNumber(s));
  const unreadable = (...s: string[]) => s.some((x) => x.trim() !== "" && parseNumber(x) === null);
  const sym = UNIT_SYMBOL[unit];
  const solvedOf = unreadable(v.a1, v.b1, v.r1) ? null : solvePercentOf(opt(v.a1), opt(v.b1), opt(v.r1), unit);
  const solvedChange = unreadable(v.a3, v.b3, v.c3) ? null : (asPoints ? null : solveChange(opt(v.a3), opt(v.b3), opt(v.c3)));
  const pointsCalc: Calc | null = asPoints ? two(v.a3, v.b3, pctPointChange) : null;
  const whatCalc = two(v.a2, v.b2, (a, b) => isWhatPct(a, b, unit));
  const byCalc = two(v.a4, v.b4, (n, p) => changeBy(n, p, dir === "up"));
  const revCalc = two(v.a5, v.b5, (f, p) => reversePct(f, p, rdir === "up"));
  const diffCalc = two(v.a6, v.b6, pctDifference);
  const changeCalc: Calc | null = pointsCalc ?? solvedChange?.calc ?? null;
  const pctofCalc: Calc | null = solvedOf?.calc ?? null;
  // One line per tab for the visit's history, and the steps of the tab being looked at.
  const ok = (c: Calc | null): c is Exclude<Calc, { error: string }> => !!c && !("error" in c);
  const line = (() => {
    if (tab === "pctof" && solvedOf && ok(pctofCalc)) {
      const p = solvedOf.field === "pct" ? pctofCalc.value : opt(v.a1)!, b = solvedOf.field === "base" ? pctofCalc.value : opt(v.b1)!, r = solvedOf.field === "result" ? pctofCalc.value : opt(v.r1)!;
      return `${fmt(p)}${sym} of ${fmt(b)} = ${fmt(r)}`;
    }
    if (tab === "iswhat" && ok(whatCalc)) return `${v.a2} is ${whatCalc.text} of ${v.b2}`;
    if (tab === "change" && ok(changeCalc)) return asPoints ? `${v.a3}% to ${v.b3}%: ${changeCalc.text}` : solvedChange!.field === "pct" ? `${v.a3} to ${v.b3}: ${changeCalc.text}` : `${solvedChange!.field === "to" ? `${v.a3} changed by ${v.c3}% = ${changeCalc.text}` : `${changeCalc.text} changed by ${v.c3}% = ${v.b3}`}`;
    if (tab === "by" && ok(byCalc)) return `${v.a4} ${dir === "up" ? "+" : "−"} ${v.b4}% = ${byCalc.text}`;
    if (tab === "rev" && ok(revCalc)) return `Original before ${rdir === "up" ? "+" : "−"}${v.b5}% giving ${v.a5}: ${revCalc.text}`;
    if (tab === "diff" && ok(diffCalc)) return `Difference between ${v.a6} and ${v.b6}: ${diffCalc.text}`;
    return "";
  })();
  const activeCalc = { pctof: pctofCalc, iswhat: whatCalc, change: changeCalc, by: byCalc, rev: revCalc, diff: diffCalc }[tab] ?? null;
  const steps: CalcStep[] = ok(activeCalc) ? activeCalc.steps ?? [] : [];
  const unitSelect = (
    <select className="border rounded px-2 py-2 bg-background text-sm" value={unit} onChange={(e) => setUnit(e.target.value as PctUnit)} aria-label="Unit: percent, per mille or basis points">
      <option value="pct">% percent</option><option value="permille">‰ per mille</option><option value="bps">bps basis points</option>
    </select>
  );

  const dirButtons = (d: "up" | "down", setD: (x: "up" | "down") => void, a: string) => (
    <select className="border rounded px-2 py-1 bg-background text-sm" value={d} onChange={(e) => setD(e.target.value as "up" | "down")} aria-label={a}>
      <option value="up">increased by</option><option value="down">decreased by</option>
    </select>
  );

  return (
    <ToolContainer>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto">
          <TabsTrigger value="pctof">% of a number</TabsTrigger>
          <TabsTrigger value="iswhat">X is what %</TabsTrigger>
          <TabsTrigger value="change">% change</TabsTrigger>
          <TabsTrigger value="by">Add / take off %</TabsTrigger>
          <TabsTrigger value="rev">Find the original</TabsTrigger>
          <TabsTrigger value="diff">% difference</TabsTrigger>
        </TabsList>

        <TabsContent value="pctof" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Field width="w-24" label={`Amount in ${UNIT_NAME[unit]}`} value={v.a1} onChange={set("a1")} /><span className="pt-2">{unit === "pct" ? "%" : sym} of</span>
            <Field label="Number" value={v.b1} onChange={set("b1")} /><span className="pt-2">=</span>
            <Field label="Result" value={v.r1} onChange={set("r1")} />
            {unitSelect}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Fill in any two boxes and leave one empty: the empty one is worked out for you.</p>
          <div className="mt-2">
            {unreadable(v.a1, v.b1, v.r1)
              ? <Result calc={null} blocked={blocked} />
              : solvedOf
                ? <>
                    <p className="text-xs text-muted-foreground mb-1" data-testid="pc-solved">{solvedOf.overdetermined ? "All three boxes are filled, so the result is recalculated from the first two. Clear a box to solve for it." : `Solved for the ${solvedOf.field === "pct" ? `amount in ${UNIT_NAME[unit]}` : solvedOf.field === "base" ? "number" : "result"}:`}</p>
                    <Result calc={pctofCalc} blocked={blocked} />
                  </>
                : <Card className="px-4 py-2 text-sm text-muted-foreground" data-testid="pc-result">Fill in two of the three boxes.</Card>}
          </div>
        </TabsContent>

        <TabsContent value="iswhat" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Field width="w-24" label="Part" value={v.a2} onChange={set("a2")} /><span className="pt-2">is what % of</span>
            <Field label="Whole" value={v.b2} onChange={set("b2")} /><span className="pt-2">=</span>
            <Result calc={whatCalc} blocked={blocked} />
            {unitSelect}
          </div>
        </TabsContent>

        <TabsContent value="change" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="pt-2">From</span><Field label="Starting value" value={v.a3} onChange={set("a3")} />
            <span className="pt-2">to</span><Field label="New value" value={v.b3} onChange={set("b3")} />
            {!asPoints && <><span className="pt-2">change %</span><Field width="w-24" label="Change in percent" value={v.c3} onChange={set("c3")} /></>}
            <span className="pt-2">=</span>
            <Result calc={changeCalc} blocked={blocked} colour />
          </div>
          <label className="flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={asPoints} onChange={(e) => setAsPoints(e.target.checked)} aria-label="Both values are percentages" />Both values are percentages (show percentage points and relative change)</label>
          <p className="text-xs text-muted-foreground mt-1">{asPoints ? "Example: 10% to 12% is +2 percentage points, which is a +20% relative change." : "Leave the change % box empty to work it out, or fill it in and leave the starting or new value empty to solve for that one."}</p>
          {!asPoints && solvedChange?.overdetermined && <p className="text-xs text-muted-foreground mt-1" data-testid="pc-solved">All three boxes are filled, so the change is recalculated from the first two.</p>}
        </TabsContent>

        <TabsContent value="by" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <Field label="Number" value={v.a4} onChange={set("a4")} />{dirButtons(dir, setDir, "Increase or decrease")}
            <Field width="w-24" label="Percentage" value={v.b4} onChange={set("b4")} /><span className="pt-2">% =</span>
            <Result calc={byCalc} blocked={blocked} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">Handy for adding tax or taking off a discount.</p>
        </TabsContent>

        <TabsContent value="rev" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="pt-2">After being</span>{dirButtons(rdir, setRdir, "Increased or decreased")}
            <Field width="w-24" label="Percentage" value={v.b5} onChange={set("b5")} /><span className="pt-2">% it is</span>
            <Field label="Final value" value={v.a5} onChange={set("a5")} /><span className="pt-2">. Original =</span>
            <Result calc={revCalc} blocked={blocked} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">For example: it costs 80 after 20% off, so what was the original price?</p>
        </TabsContent>

        <TabsContent value="diff" className="mt-4">
          <div className="flex items-start gap-3 flex-wrap">
            <span className="pt-2">Difference between</span><Field label="First value" value={v.a6} onChange={set("a6")} />
            <span className="pt-2">and</span><Field label="Second value" value={v.b6} onChange={set("b6")} /><span className="pt-2">=</span>
            <Result calc={diffCalc} blocked={blocked} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">Compared with the average of the two, so the order doesn't matter. Use "% change" when one value is the starting point.</p>
        </TabsContent>
      </Tabs>
      {line && (
        <Suspense fallback={null}>
          <PercentageExtras summary={line} steps={steps} gate={gate} />
        </Suspense>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
